import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Languages, Calendar, MessageCircle, Phone, CheckCircle2, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1742">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1743" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1744">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1745">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-1746">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-1747">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1748" /> : <Plus className="w-4 h-4" data-id="element-1749" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1750">
        <p className="text-gray-600 leading-relaxed" data-id="element-1751">{answer}</p>
      </div>
    </div>;
}
export function EmergencyDentistLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Что делать, если сильно болит зуб?',
    a: 'Как можно скорее свяжитесь с нами для неотложного осмотра. Мы определим причину боли и предложим лечение. Не грейте больное место!'
  }, {
    q: 'Принимаете ли вы срочно в тот же день?',
    a: 'Да, мы всегда стараемся найти время для пациентов с острой болью в тот же день. Напишите в WhatsApp/Telegram или позвоните.'
  }, {
    q: 'Сколько стоит экстренный прием?',
    a: 'Стоимость зависит от необходимых процедур и диагностики. Экстренная консультация — £60. После осмотра мы объясним варианты лечения и цены.'
  }, {
    q: 'Какие симптомы считаются неотложными?',
    a: 'Сильная зубная боль, отек лица или десен, гной, повышенная температура, боль при глотании, травма зуба (скол, вывих), кровотечение после удаления.'
  }, {
    q: 'Что делать, если выбили зуб?',
    a: 'Найдите зуб, держите его за коронку (не за корень!), промойте водой, если грязный. Попробуйте вставить обратно в лунку или положите в молоко/слюну. Обратитесь к врачу в течение 30-60 минут!'
  }, {
    q: 'Работаете ли вы в выходные?',
    a: 'Мы работаем с понедельника по субботу. В неотложных случаях напишите нам в WhatsApp, мы попробуем помочь или посоветовать.'
  }, {
    q: 'Можно ли получить помощь на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке. Вы сможете точно описать свои симптомы.'
  }, {
    q: 'Где вы находитесь?',
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington), рядом с Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1752">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1753" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1754" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1755" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1756">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1757" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1758">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1759">
            <div className="space-y-6" data-id="element-1760">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1761">
                <AlertCircle className="w-4 h-4" data-id="element-1762" />
                Неотложная помощь
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1763">
                Неотложная стоматологическая помощь в Лондоне –{' '}
                <span className="text-red-600" data-id="element-1764">Dentico Clinic</span>
                <br data-id="element-1765" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1766">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1767">
                Острая боль? Сломанный зуб? Отек? Мы принимаем пациентов с
                острой болью в тот же день. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-1768">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1769">
                  <Clock className="w-4 h-4 text-[#2F4A3F]" data-id="element-1770" />
                  Срочный приём
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1771">
                  <MapPin className="w-4 h-4 text-[#2F4A3F]" data-id="element-1772" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1773">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-1774" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1775">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1776">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-1777">
                    <MessageCircle className="w-5 h-5" data-id="element-1778" />
                    Написать в WhatsApp
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1779">
                  <Button variant="secondary" size="lg" className="w-full gap-2" data-id="element-1780">
                    <Phone className="w-5 h-5" data-id="element-1781" />
                    +44 7345 197820
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1782">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1783"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt="Неотложная стоматология Лондон" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1784" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1785">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1786">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1787">
            Когда нужна неотложная помощь?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1788">
            {[{
            title: 'Сильная зубная боль',
            desc: 'Боль, которая не проходит от обезболивающих, пульсирующая или ночная боль.'
          }, {
            title: 'Отек лица',
            desc: 'Припухлость щеки или десны может свидетельствовать о серьезной инфекции.'
          }, {
            title: 'Травма зуба',
            desc: 'Сломанный, сколотый или выбитый зуб в результате удара или падения.'
          }, {
            title: 'Потеря пломбы/коронки',
            desc: 'Если выпала пломба или коронка, и зуб стал чувствительным или острым.'
          }, {
            title: 'Кровотечение',
            desc: 'Кровотечение из десны после удаления зуба, которое не останавливается.'
          }, {
            title: 'Боль при глотании',
            desc: 'Затрудненное глотание или дыхание — это признак опасного распространения инфекции.'
          }].map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1789">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1790">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1791" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1792">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1793">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1794">
          <div className="text-center mb-10" data-id="element-1795">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1796">
              Что делать до визита к врачу?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1797">
              Советы первой помощи, пока вы ждете приема.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1798">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1799">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1800">
                При зубной боли
              </h3>
              <p className="text-gray-600" data-id="element-1801">
                Примите обезболивающее (ибупрофен или парацетамол), прополощите
                рот теплой водой. Не прикладывайте аспирин к десне и не грейте
                больное место!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1802">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1803">
                Если выбили зуб
              </h3>
              <p className="text-gray-600" data-id="element-1804">
                Держите зуб только за коронку (белую часть). Если грязный —
                промойте молоком или водой. Попробуйте вставить обратно в лунку
                или положите в молоко/слюну. Время критично — у вас есть 30-60
                минут!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1805">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1806">
                При отеке
              </h3>
              <p className="text-gray-600" data-id="element-1807">
                Приложите холодный компресс к щеке снаружи. Это уменьшит отек и
                боль. Не используйте тепло!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1808">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1809">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1810">
            Как мы работаем в экстренных случаях
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1811">
            {[{
            step: '01',
            title: 'Связь',
            desc: 'Напишите нам в WhatsApp или позвоните. Опишите симптомы.'
          }, {
            step: '02',
            title: 'Оценка',
            desc: 'Мы оценим срочность и найдем ближайшее время для визита.'
          }, {
            step: '03',
            title: 'Визит',
            desc: 'Осмотр, рентген-диагностика и снятие острой боли.'
          }, {
            step: '04',
            title: 'План',
            desc: 'Если требуется дальнейшее лечение, мы составим план действий.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1812">
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1813">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1814">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1815">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1816">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1817">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1818">
          <div className="text-center mb-12" data-id="element-1819">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1820">
              Виды неотложной помощи
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1821">
            {[{
            title: 'Снятие острой боли',
            desc: 'Медикаментозная обработка и временная пломба.'
          }, {
            title: 'Удаление зуба',
            desc: 'Если зуб невозможно спасти или он является источником инфекции.'
          }, {
            title: 'Лечение каналов',
            desc: 'Удаление воспаленного нерва (пульпэктомия) для спасения зуба.'
          }, {
            title: 'Фиксация коронки',
            desc: 'Цементирование выпавшей коронки или моста.'
          }, {
            title: 'Вскрытие абсцесса',
            desc: 'Дренаж гноя для снятия отека и боли.'
          }, {
            title: 'Шинирование зубов',
            desc: 'Фиксация подвижных зубов после травмы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1822">
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1823" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1824">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1825">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1826">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1827">
          <div className="text-center mb-10" data-id="element-1828">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1829">
              Частые вопросы
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1830">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1831" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1832">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1833">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1834">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1835">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-1836">
              <Button variant="outline" size="sm" data-id="element-1837">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/lechenie-kanalov-london" data-id="element-1838">
              <Button variant="outline" size="sm" data-id="element-1839">
                Лечение каналов
              </Button>
            </Link>
            <Link to="/ru/services/extraction" data-id="element-1840">
              <Button variant="outline" size="sm" data-id="element-1841">
                Удаление зубов
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-1842">
              <Button variant="outline" size="sm" data-id="element-1843">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-1844">
              <Button variant="outline" size="sm" data-id="element-1845">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1846">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1847">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1848">
            Нужна срочная помощь?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1849">
            Не ждите, пока боль усилится. Свяжитесь с нами прямо сейчас.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1850">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1851">
              <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-1852">
                <MessageCircle className="w-5 h-5" data-id="element-1853" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1854">
              <Button variant="outlineInverse" size="lg" className="w-full gap-2" data-id="element-1855">
                <Phone className="w-5 h-5" data-id="element-1856" />
                +44 7345 197820
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}